import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  addSpaceResourceMutation,
  errorMessage,
  giveSpaceAccessMutation,
  listSpaceResourcesOptions,
  listSpaceResourcesQueryKey,
  removeSpaceAccessMutation,
  removeSpaceResourceMutation,
  type SpaceResourceResponse,
  updateSpaceResourceMutation,
} from "@/api";
import { useRefreshSpace } from "../useSpaceSettings";

export type SpaceItemType = "agent" | "data_collection" | "micro_app" | "flow";
export type Visibility = "private" | "space";

/** One kind of item in a space (as this user sees it) plus add / update / remove. */
const TARGET_FIELD: Record<SpaceItemType, (uuid: string) => Record<string, string>> = {
  agent: (uuid) => ({ agentUuid: uuid }),
  data_collection: (uuid) => ({ dataCollectionUuid: uuid }),
  micro_app: (uuid) => ({ microAppUuid: uuid }),
  flow: (uuid) => ({ flowUuid: uuid }),
};

export function useSpaceItems(spaceUuid: string, type: SpaceItemType) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.items" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const refreshSpace = useRefreshSpace(spaceUuid);
  const path = { space_uuid: spaceUuid };
  const items = useQuery({
    ...listSpaceResourcesOptions({ path, query: { type } }),
    select: (r) => r.data,
  });
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listSpaceResourcesQueryKey({ path }) }),
      queryClient.invalidateQueries({ queryKey: [{ _id: "spaceAccessImpact" }] }),
      refreshSpace(),
    ]);
  const onError = (fallback: string) => (error: unknown) => toast.error(errorMessage(error, fallback));

  const add = useMutation({ ...addSpaceResourceMutation(), onSettled: refresh });
  const update = useMutation({ ...updateSpaceResourceMutation(), onSettled: refresh, onError: onError(t("failed")) });
  const remove = useMutation({
    ...removeSpaceResourceMutation(),
    onSettled: refresh,
    onSuccess: () => toast.success(t("removed")),
    onError: onError(t("failed")),
  });

  const grant = useMutation({
    ...giveSpaceAccessMutation(),
    onSettled: refresh,
    onSuccess: () => toast.success(t("access_toast")),
    onError: onError(t("failed")),
  });
  const revoke = useMutation({
    ...removeSpaceAccessMutation(),
    onSettled: refresh,
    onSuccess: () => toast.success(t("accessRemoved_toast")),
    onError: onError(t("failed")),
  });

  return {
    items,
    add: (targetUuid: string, visibility: Visibility) =>
      add.mutateAsync({
        path,
        body: {
          resourceType: type,
          visibility,
          ...TARGET_FIELD[type](targetUuid),
        },
      }),
    setVisibility: (item: SpaceResourceResponse, visibility: Visibility) =>
      update.mutate(
        { path: { ...path, resource_uuid: item.uuid }, body: { visibility } },
        { onSuccess: () => toast.success(t(visibility === "space" ? "shared_toast" : "private_toast")) },
      ),
    setPinned: (item: SpaceResourceResponse, isPinned: boolean) =>
      update.mutate({ path: { ...path, resource_uuid: item.uuid }, body: { isPinned } }),
    remove: (item: SpaceResourceResponse) => remove.mutate({ path: { ...path, resource_uuid: item.uuid } }),
    /** Every member can use what it points to (also shares it). */
    giveAccess: (item: SpaceResourceResponse) => grant.mutate({ path: { ...path, resource_uuid: item.uuid } }),
    removeAccess: (item: SpaceResourceResponse) => revoke.mutate({ path: { ...path, resource_uuid: item.uuid } }),
  };
}

/** The uuid of what an item points to (agent, data source, micro-app or workflow). */
export const targetOf = (item: SpaceResourceResponse) =>
  item.agentUuid ?? item.dataCollectionUuid ?? item.microAppUuid ?? item.flow?.uuid ?? "";
export const nameOf = (item: SpaceResourceResponse) =>
  item.agentName ?? item.dataCollectionName ?? item.microAppName ?? item.flow?.name ?? item.title ?? "";
